import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  createForm,
  duplicateForm,
  formBuilderShellQuery,
  formEditorQuery,
  formListQuery,
  paletteField,
  removeForm,
  saveForm,
  type ConfiguredField,
  type FormListEntry,
  type PaletteField,
  type SavedField,
} from '../../api/settingsEaseedeskFormBuilder'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Form builder.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_form_builder/index.php` + `form_list.php` + `edit_form.php` +
 * `sortable_field.php`, si gest cu gest fata de scripturile lor.
 *
 * ---------------------------------------------------------------------------
 * ORDINEA CAMPURILOR E ORDINEA DIN LISTA
 * ---------------------------------------------------------------------------
 *
 * In CI3 fiecare rand purta un `<input class="field_order" type="hidden">`, iar
 * scriptul le renumerota la fiecare mutare. Un camp adaugat primea `0`, deci
 * doua campuri puteau avea acelasi numar - si atunci `ORDER BY field_order` le
 * aseza altfel la fiecare deschidere. Aici ordinea e POZITIA din lista, si se
 * numeroteaza la salvare, in backend.
 *
 * ---------------------------------------------------------------------------
 * MUTAREA: TRAGERE, SI DOUA SAGETI
 * ---------------------------------------------------------------------------
 *
 * Machetele foloseau `sortable` din jQuery UI. Tragerea e aici cu HTML5
 * drag-and-drop, dar lista are si sus/jos: pe o lista de douazeci de campuri,
 * mutarea unuia din capat pana in celalalt cu tragerea inseamna un derulaj cu
 * mouse-ul apasat, iar cu tastatura nu se poate deloc.
 */

const VISIBILITY = [
  { value: 0, label: 'All' },
  { value: 1, label: 'User' },
  { value: 2, label: 'Admin' },
]

/** Randul unui camp asezat pe formular: `drag-element` din machete. */
export function FieldRow({
  field,
  first,
  last,
  onChange,
  onRemove,
  onMove,
  onDragStart,
  onDragOver,
  onDrop,
}: {
  field: ConfiguredField
  first: boolean
  last: boolean
  onChange(next: ConfiguredField): void
  onRemove(): void
  onMove(delta: number): void
  onDragStart(): void
  onDragOver(e: DragEvent): void
  onDrop(): void
}) {
  return (
    <li className="list-li" draggable onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}>
      <div
        className="row drag-element"
        data-field={field.key}
        style={{
          border: '1px solid #DDD',
          padding: '10px 0',
          margin: 0,
          marginBottom: 10,
          backgroundColor: '#FFF',
          cursor: 'move',
          position: 'relative',
        }}
      >
        <div className="col-md-4">
          <h5 style={{ marginBottom: 10 }}>{field.field_name}</h5>
          <label className="label label-primary">{field.field_type}</label>
        </div>
        <div className="col-md-3">
          <div style={{ marginBottom: 4 }}>
            {field.always ? (
              <label>
                <i className="fas fa-info-circle color-blue" aria-hidden="true"></i> Always Required
              </label>
            ) : (
              <label>
                Required{' '}
                <input
                  type="checkbox"
                  className="is_required"
                  value="1"
                  checked={field.required}
                  onChange={(e) => onChange({ ...field, required: e.target.checked })}
                />
              </label>
            )}
          </div>
          <div>
            <label>Visibility</label>{' '}
            <select
              value={field.visibility}
              onChange={(e) => onChange({ ...field, visibility: Number(e.target.value) })}
            >
              {VISIBILITY.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-md-4">
          {field.default_values.length > 0 ? (
            <>
              <label>Default value</label>
              <div className="color-light-grey" style={{ fontSize: 12 }}>
                {field.default_values.join(', ')}
              </div>
            </>
          ) : null}
        </div>
        <div className="col-md-1">
          <div className="btn-group pull-right">
            <button
              type="button"
              className="btn btn-default btn-xs"
              disabled={first}
              title="Move up"
              onClick={() => onMove(-1)}
            >
              <i className="fas fa-arrow-up" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className="btn btn-default btn-xs"
              disabled={last}
              title="Move down"
              onClick={() => onMove(1)}
            >
              <i className="fas fa-arrow-down" aria-hidden="true"></i>
            </button>
            <button type="button" className="btn btn-danger btn-xs remove-field" data-field={field.key} onClick={onRemove}>
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </li>
  )
}

/** Paleta din dreapta: campurile standard si cele proprii, cu bifa. */
export function Palette({
  title,
  fields,
  chosen,
  open,
  onToggleOpen,
  onToggle,
  search,
  onSearch,
  labelClass,
  empty,
}: {
  title: string
  fields: PaletteField[]
  chosen: Set<string>
  open: boolean
  onToggleOpen(): void
  onToggle(field: PaletteField, on: boolean): void
  search?: string
  onSearch?(value: string): void
  labelClass: string
  /** Ce se scrie cand lista e goala de tot (nu doar dupa cautare). */
  empty?: ReactNode
}) {
  const shown =
    search === undefined || search.trim() === ''
      ? fields
      : fields.filter((f) => f.field_name.toLowerCase().includes(search.trim().toLowerCase()))

  return (
    <div className="panel panel-shadow">
      <header
        className="panel-heading"
        style={{ border: '1px solid #ddd', padding: '0 10px', backgroundColor: '#f5f5f5' }}
      >
        <a
          href="#"
          className={open ? '' : 'collapsed'}
          onClick={(e) => {
            e.preventDefault()
            onToggleOpen()
          }}
        >
          <i className="collapse-caret fa fa-angle-up"></i> {title}{' '}
          <span className="badge palette-count">{fields.length}</span>
        </a>
      </header>
      <div className={`panel-collapse collapse${open ? ' in' : ''}`}>
        <div className="panel-body" style={{ border: '1px solid #ddd' }}>
          {onSearch ? (
            <div className="form-group input-group input-group-sm" style={{ marginBottom: 8, width: '100%' }}>
              <input
                type="text"
                className="form-control input-sm custom-field-search"
                placeholder="Search custom fields…"
                autoComplete="off"
                value={search ?? ''}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
          ) : null}
          <ul className="list-group custom-field-list">
            {shown.map((field) => (
              <li className="list-group-item" key={field.key} data-name={field.field_name.toLowerCase()}>
                <input
                  type="checkbox"
                  className="add-field-to-form"
                  data-field={field.key}
                  value={field.ID}
                  checked={chosen.has(field.key)}
                  onChange={(e) => onToggle(field, e.target.checked)}
                />{' '}
                {field.field_label !== '' ? field.field_label : field.field_name}
                <label className={`label ${labelClass} pull-right`}>{field.field_type}</label>
              </li>
            ))}
          </ul>
          {fields.length === 0 && empty ? (
            <div className="color-light-grey palette-empty" style={{ fontSize: 12, margin: '0 0 8px' }}>
              {empty}
            </div>
          ) : shown.length === 0 ? (
            <p className="color-light-grey" style={{ fontSize: 12, margin: '0 0 8px' }}>
              Nothing here.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** Editorul unui formular: lista asezata in stanga, paleta in dreapta. */
function FormEditorPanel({
  form,
  canCreateCustomFields,
  onClose,
  onSaved,
}: {
  form: number
  canCreateCustomFields: boolean
  onClose(): void
  onSaved(): void
}) {
  const editor = useQuery(formEditorQuery(form))
  const [name, setName] = useState('')
  const [fields, setFields] = useState<ConfiguredField[]>([])
  const [dragged, setDragged] = useState<number | null>(null)
  const [openPanel, setOpenPanel] = useState<'standard' | 'custom'>('standard')
  const [search, setSearch] = useState('')

  /* Lista asezata se ia de la server O DATA pe formular. Paleta se reciteste
     cand se deschide panoul de campuri proprii - un camp facut intre timp in
     Custom fields (alta fila a browserului) apare fara sa se piarda ce s-a
     asezat si nu s-a salvat inca. Se asteapta cererea de la deschidere, nu
     copia din cache: dupa o salvare, copia aia e formularul de dinainte. */
  const loaded = useRef(false)
  useEffect(() => {
    if (!editor.data || !editor.isFetchedAfterMount || loaded.current) return
    loaded.current = true
    setName(editor.data.formDetails.form_name)
    setFields(editor.data.configured_fields)
  }, [editor.data, editor.isFetchedAfterMount])

  const chosen = new Set(fields.map((f) => f.key))

  const save = useMutation({
    mutationFn: () =>
      saveForm(
        form,
        name.trim(),
        fields.map<SavedField>((f) => ({
          ID: f.ID,
          custom_field: f.custom_field,
          required: f.required,
          visibility: f.visibility,
        })),
      ),
    onSuccess: () => {
      toast('Form saved.', 'Success')
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  /* `get_sortable_field`: campul bifat se cere de la server, ca sa fie asezat
     cu numele si felul lui - nu se ghiceste din randul paletei. */
  const add = useMutation({
    mutationFn: (field: PaletteField) => paletteField(field.ID, field.custom_field ? 'custom' : 'standard'),
    onSuccess: (answer) => {
      const field = answer.field
      setFields((current) =>
        current.some((f) => f.key === field.key)
          ? current
          : [
              ...current,
              {
                config_id: 0,
                ID: field.ID,
                key: field.key,
                custom_field: field.custom_field,
                field_name: field.field_name,
                field_label: field.field_label,
                field_type: field.field_type,
                field_unique_id: field.field_unique_id,
                required: false,
                always: field.always,
                visibility: 0,
                order: current.length,
                options: [],
                default_values: [],
              },
            ],
      )
    },
    onError: () => toast('Could not add the field.', 'Error', 'error'),
  })

  if (editor.error) return <LoadFailed error={editor.error} onRetry={() => void editor.refetch()} what="This form" />
  if (editor.isPending || !editor.isFetchedAfterMount) return <Spinner />
  if (!editor.data) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> This form could not be opened.
      </div>
    )
  }

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= fields.length) return
    const next = [...fields]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    setFields(next)
  }

  return (
    <div className="row" id="edit-created-form">
      <div className="col-md-7">
        <div style={{ marginBottom: 20 }}>
          <button
            className="btn btn-primary"
            type="button"
            id="save-edit-form"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            <i className="fa fa-check" aria-hidden="true"></i> Save
          </button>{' '}
          <button className="btn btn-default" type="button" id="cancel-edit-form" onClick={onClose}>
            <i className="fas fa-times" aria-hidden="true"></i> Cancel
          </button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <input
            type="text"
            className="form-control"
            name="form-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <ul className="sortable" style={{ border: '1px solid #ddd', padding: 20, backgroundColor: '#f5f5f5' }}>
          {fields.map((field, index) => (
            <FieldRow
              key={field.key}
              field={field}
              first={index === 0}
              last={index === fields.length - 1}
              onChange={(next) => setFields(fields.map((f) => (f.key === next.key ? next : f)))}
              onRemove={() => setFields(fields.filter((f) => f.key !== field.key))}
              onMove={(delta) => move(index, delta)}
              onDragStart={() => setDragged(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragged === null || dragged === index) return
                move(dragged, index - dragged)
                setDragged(null)
              }}
            />
          ))}
          {fields.length === 0 ? (
            <li className="color-light-grey" style={{ fontSize: 12 }}>
              No field on this form yet. Tick one on the right.
            </li>
          ) : null}
        </ul>
      </div>

      <div className="col-md-5">
        <div className="panel-group" id="accordion" style={{ margin: 0 }}>
          <Palette
            title="Fields"
            fields={editor.data.fields}
            chosen={chosen}
            open={openPanel === 'standard'}
            onToggleOpen={() => setOpenPanel('standard')}
            onToggle={(field, on) =>
              on ? add.mutate(field) : setFields((current) => current.filter((f) => f.key !== field.key))
            }
            labelClass="label-info"
          />
          <Palette
            title="Custom fields"
            fields={editor.data.custom_fields}
            chosen={chosen}
            open={openPanel === 'custom'}
            onToggleOpen={() => {
              setOpenPanel('custom')
              void editor.refetch()
            }}
            onToggle={(field, on) =>
              on ? add.mutate(field) : setFields((current) => current.filter((f) => f.key !== field.key))
            }
            search={search}
            onSearch={setSearch}
            labelClass="label-primary"
            empty={
              <>
                This instance has no custom fields yet. They are made in{' '}
                {canCreateCustomFields ? (
                  <a href="/app/settings/easeedesk/ticket_custom_fields">Forms &rarr; Custom fields</a>
                ) : (
                  <strong>Forms &rarr; Custom fields</strong>
                )}
                , and then show up here to be ticked onto the form.
              </>
            }
          />
        </div>
      </div>
    </div>
  )
}

/** `newFormModal` din macheta. */
function NewFormModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (!open) return
    setName('')
    setDescription('')
  }, [open])

  const create = useMutation({
    mutationFn: () => createForm(name.trim(), description.trim()),
    onSuccess: () => {
      toast('New form created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="newFormModal" labelledBy="newFormModalLabel" open={open} onClose={onClose} title="Create form">
      <form
        id="new-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          create.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="form-name" className="control-label">
            Form name
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id="form-name"
            name="form_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="form-description" className="control-label">
            Description
          </label>
          <textarea
            id="form-description"
            className="form-control input-sm"
            name="form_description"
            style={{ maxWidth: '100%' }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary submit-new-form" disabled={create.isPending}>
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** Randul din lista: numele, cine il foloseste, si cele trei butoane. */
function FormRow({
  form,
  onEdit,
  onDuplicate,
  onRemove,
}: {
  form: FormListEntry
  onEdit(): void
  onDuplicate(): void
  onRemove(): void
}) {
  return (
    <li className="list-group-item">
      <i className="fas fa-list-alt" aria-hidden="true"></i> {form.form_name}
      {form.used_by.length > 0 ? (
        <span
          className="color-light-grey"
          style={{ fontSize: 11, marginLeft: 6 }}
          title={form.used_by.join(', ')}
        >
          <i className="fas fa-link" aria-hidden="true"></i> used by {form.used_by.length}:{' '}
          {form.used_by.slice(0, 3).join(', ')}
          {form.used_by.length > 3 ? '…' : ''}
        </span>
      ) : (
        <span
          className="color-light-grey"
          style={{ fontSize: 11, marginLeft: 6 }}
          title="No ticket type and status uses this form yet"
        >
          <i className="fas fa-unlink" aria-hidden="true"></i> not assigned
        </span>
      )}
      <div className="btn-group pull-right" role="group">
        <button
          type="button"
          className="btn btn-xs btn-default duplicate-ticket-form"
          title="Duplicate this form with all its fields"
          onClick={onDuplicate}
        >
          <i className="far fa-clone" aria-hidden="true"></i>
        </button>
        <button type="button" className="btn btn-xs btn-primary edit-ticket-form" onClick={onEdit}>
          <i className="fas fa-pencil-alt" aria-hidden="true"></i>
        </button>
        <button type="button" className="btn btn-xs btn-danger remove-ticket-form" onClick={onRemove}>
          <i className="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>
    </li>
  )
}

/**
 * Ecranul intreg. Fila `new_ticket_form_builder` era acelasi ecran cu alt
 * drept; din 20 octombrie 2026 adresa ei trimite aici (router.tsx).
 */
export function TicketFormBuilderPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(formBuilderShellQuery())
  const list = useQuery(formListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState(0)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_form_builder'] })
  }

  const duplicate = useMutation({
    mutationFn: (form: number) => duplicateForm(form),
    onSuccess: (answer) => {
      toast(`Copy created: ${answer.form_name ?? ''}`, 'Success')
      reload()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const confirmRemove = useMutation({
    mutationFn: (form: number) => removeForm(form, true),
    onSuccess: (_answer, form) => {
      toast('Form removed.', 'Success')
      if (edited === form) setEdited(0)
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  /* Prima cerere intreaba, a doua sterge. Un formular scos din uz cat timp e
     atribuit unui status lasa pasul fara macheta - si asta se vede abia la
     deschiderea unui tichet, nu aici. */
  const remove = useMutation({
    mutationFn: (form: number) => removeForm(form, false),
    onSuccess: (answer, form) => {
      if (answer.confirm) {
        const used = (answer.used_by ?? []).join(', ')
        if (!window.confirm(`This form is used by: ${used}.\nRemove it anyway?`)) return
        confirmRemove.mutate(form)
        return
      }
      toast('Form removed.', 'Success')
      if (edited === form) setEdited(0)
      reload()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="The form builder" />
  if (!shell.data) return <Spinner />

  const forms = list.data?.form_list ?? []

  return (
    <div>
      <h4>
        <strong>Form</strong> builder
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage ticket <strong>forms</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create new form
      </button>
      <hr />

      <div className="row" id="ticket-form-list">
        <div className="col-md-12">
          <ul id="forms-list" className="list-group">
            {list.error ? (
              <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The form list" />
            ) : !list.data ? (
              <Spinner />
            ) : forms.length === 0 ? (
              <li>
                <div className="well flip-bg-white" style={{ backgroundColor: '#D4EEFF' }}>
                  <h4>
                    <i className="fas fa-exclamation-triangle"></i> <strong>INFO</strong>
                  </h4>
                  <p>
                    Form list is empty. Click <strong>+ Create new form</strong> button.
                  </p>
                  <div className="flip"></div>
                </div>
              </li>
            ) : (
              forms.map((form) => (
                <FormRow
                  key={form.ID}
                  form={form}
                  onEdit={() => setEdited(form.ID)}
                  onDuplicate={() => duplicate.mutate(form.ID)}
                  onRemove={() => {
                    if (!window.confirm('Are you sure?\nYou will not be able to recover this form!')) return
                    remove.mutate(form.ID)
                  }}
                />
              ))
            )}
          </ul>
        </div>
      </div>

      {edited > 0 ? (
        <FormEditorPanel
          key={edited}
          form={edited}
          canCreateCustomFields={shell.data.canCreateCustomFields}
          onClose={() => setEdited(0)}
          onSaved={() => {
            setEdited(0)
            reload()
          }}
        />
      ) : null}

      <NewFormModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
    </div>
  )
}
